// src/admin/AdminDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Toolbar,
  AppBar,
  Typography,
  Button,
  IconButton,
  Avatar,
  Chip,
  Divider,
  Container,
  Paper,
} from '@mui/material';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';

import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import TokenIcon from '@mui/icons-material/Token';
import SummarizeIcon from '@mui/icons-material/Summarize';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import PaymentIcon from '@mui/icons-material/Payment';
import ListAltIcon from '@mui/icons-material/ListAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import NotificationsIcon from '@mui/icons-material/Notifications';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

// Import the new logo component
import DreamyAppsLogo from '../components/DreamyAppsLogo';

// Screens
import AdminDashboardHome from './AdminDashboardHome';
import AdminUsersList from './AdminUsersList';
import AdminUserDetails from './AdminUserDetails';
import AdminVideosList from './AdminVideosList';
import AdminPendingVideos from './AdminPendingVideos';
// ❌ AdminVideoPlayer removed
import AdminTokenRewards from './AdminTokenRewards';
import AdminConversionSummary from './AdminConversionSummary';
import AdminConversionHistory from './AdminConversionHistory';
// ❌ AdminAppRegions removed
import AdminAdPayment from './AdminAdPayment';
import AdminAdPaymentsList from './AdminAdPaymentsList';
import AdminWithdrawals from './AdminWithdrawals';
import AdminPaymentMethods from './AdminPaymentMethods';
import AdminVideoCategories from './AdminVideoCategories';
import AdminCountryKYC from './AdminCountryKYC';
import AdminPendingClicks from './AdminPendingClicks';
import AdminJazzCash from './AdminJazzCash';
import AdminReferralRebalance from './AdminReferralRebalance';

const drawerWidth = 260;

function AdminDashboard({ onLogout, darkMode, onToggleDarkMode }) {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  
  // State for mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);
  
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // Close mobile drawer when navigating
  const handleMobileNavClick = () => {
    setMobileOpen(false);
  };

  // Load user profile from token
  useEffect(() => {
    const loadUserProfile = () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          setUserProfile({
            id: decoded.userId,
            username: decoded.username || '',
            firstName: decoded.firstName || '',
            lastName: decoded.lastName || '',
            email: decoded.email || '',
          });
        }
      } catch (error) {
        console.error('Error loading user profile:', error);
      }
    };

    loadUserProfile();
  }, []);

  const menuItems = [
    { key: 'dashboard', label: 'Dashboard Home', icon: <DashboardIcon /> },
    { key: 'users', label: 'Users List', icon: <PeopleIcon /> },
    { key: 'videos', label: 'Videos List', icon: <VideoLibraryIcon /> },
    { key: 'pending-videos', label: 'Pending Videos', icon: <NotificationsIcon /> },
    { key: 'token-rewards', label: 'Token Rewards', icon: <TokenIcon /> },
    { key: 'conversion-summary', label: 'Conversion Summary', icon: <SummarizeIcon /> },
    { key: 'conversion-history', label: 'Conversion History', icon: <HistoryIcon /> },
    // ❌ App Regions item removed
    { key: 'ad-payment', label: 'Ad Payment Entry', icon: <PaymentIcon /> },
    { key: 'ad-payments-list', label: 'Ad Payments List', icon: <ListAltIcon /> },
    { key: 'withdrawals', label: 'Withdrawals', icon: <AccountBalanceWalletIcon /> },
    { key: 'payment-methods', label: 'Payment Methods', icon: <CreditCardIcon /> },
    { key: 'video-categories', label: 'Video Categories', icon: <VideoLibraryIcon /> },
    { key: 'country-kyc', label: 'Country KYC', icon: <VerifiedUserIcon /> },
    { key: 'pending-clicks', label: 'Pending Clicks', icon: <SettingsIcon /> },
    { key: 'jazzcash', label: 'JazzCash Export', icon: <AccountBalanceWalletIcon /> },
    { key: 'referral-rebalance', label: 'Referral rebalance', icon: <AccountTreeIcon /> },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Top AppBar */}
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 1, sm: 3 }, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              color="inherit"
              onClick={handleDrawerToggle}
              sx={{ 
                display: { xs: 'block', sm: 'none' },
                mr: 1,
                transition: 'all 0.3s ease',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <DreamyAppsLogo size="medium" showText={true} variant="minimal" />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton 
              onClick={onToggleDarkMode} 
              color="inherit" 
              sx={{ 
                mr: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.1) rotate(180deg)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              {darkMode ? <Brightness7Icon /> : <Brightness4Icon />}
            </IconButton>
            <IconButton 
              color="inherit" 
              sx={{ 
                mr: 1,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.1)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
            >
              <NotificationsIcon />
            </IconButton>
            
            <Divider orientation="vertical" flexItem sx={{ mx: 1, borderColor: 'rgba(255,255,255,0.2)' }} />
            
            {/* Admin Profile Section */}
            {userProfile && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                px: 2,
                py: 0.5,
                borderRadius: 3,
                background: 'rgba(255,255,255,0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.2)',
              }}>
                <Avatar 
                  sx={{ 
                    width: 36, 
                    height: 36, 
                    bgcolor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '0.9rem',
                  }}
                >
                  {userProfile.firstName?.[0]?.toUpperCase() || userProfile.username?.[0]?.toUpperCase() || 'A'}
                </Avatar>
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                    {userProfile.firstName && userProfile.lastName 
                      ? `${userProfile.firstName} ${userProfile.lastName}`
                      : userProfile.username}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
                    Admin • {userProfile.email}
                  </Typography>
                </Box>
              </Box>
            )}
            
            <Button 
              variant="outlined" 
              sx={{ 
                display: { xs: 'none', md: 'flex' }, // Full button on desktop
                ml: 1,
                mr: 1,
                color: 'white',
                borderColor: 'rgba(255,255,255,0.5)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  borderColor: 'white',
                  background: 'rgba(255,255,255,0.15)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(255,255,255,0.2)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
              onClick={() => navigate('/dashboard')}
            >
              User Dashboard
            </Button>
            
            {/* User Dashboard Icon Button - Mobile Only */}
            <IconButton
              color="inherit"
              onClick={() => navigate('/dashboard')}
              sx={{ 
                display: { xs: 'block', md: 'none' }, // Icon only on mobile
                ml: 0.5,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.1)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
              title="User Dashboard"
            >
              <PersonIcon />
            </IconButton>
            
            {/* Logout Button - Full on Desktop, Icon only on Mobile */}
            <Button 
              variant="contained" 
              sx={{ 
                display: { xs: 'none', md: 'flex' }, // Full button on desktop
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.3)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.3)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 25px rgba(255,255,255,0.2)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
              }}
              onClick={onLogout}
              startIcon={<LogoutIcon />}
            >
              Logout
            </Button>
            
            {/* Logout Icon Button - Mobile Only */}
            <IconButton
              color="inherit"
              onClick={onLogout}
              sx={{ 
                display: { xs: 'block', md: 'none' }, // Icon only on mobile
                ml: 0.5,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  background: 'rgba(255,255,255,0.15)',
                  transform: 'scale(1.1)',
                },
                '&:active': {
                  transform: 'scale(0.95)',
                },
              }}
              title="Logout"
            >
              <LogoutIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer (Temporary) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', opacity: 0.9 }}>
          <Chip 
            label="Admin Dashboard" 
            size="small" 
            icon={<AdminPanelSettingsIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              fontWeight: 600,
              mb: 2,
              width: '100%',
              height: '32px',
              fontSize: '0.8rem'
            }} 
          />
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: 'text.primary', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em', 
            fontSize: '0.8rem',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Navigation Menu
          </Typography>
        </Box>
        <List sx={{ px: 2, py: 3 }}>
          {menuItems.map((item, index) => (
            <ListItemButton
              key={item.key}
              component={NavLink}
              to={`/admin/${item.key}`}
              onClick={handleMobileNavClick}
              sx={{
                borderRadius: 3,
                mb: 1.5,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                position: 'relative',
                overflow: 'hidden',
                "&:hover": {
                  backgroundColor: "rgba(102, 126, 234, 0.12)",
                  transform: 'translateX(8px) scale(1.02)',
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                },
                "&.active": {
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: "white",
                  fontWeight: "bold",
                  boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                  transform: 'translateX(4px)',
                  "& .MuiListItemIcon-root": {
                    color: "white",
                    transform: 'scale(1.1)',
                  },
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          ))}
          
          {/* User Dashboard Button in Mobile Drawer */}
          <Divider sx={{ my: 1.5, borderColor: 'divider' }} />
          <ListItemButton
            onClick={() => {
              handleMobileNavClick();
              navigate('/dashboard');
            }}
            sx={{
              borderRadius: 3,
              mb: 1.5,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              fontWeight: 600,
              boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)',
              "&:hover": {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                transform: 'translateX(8px) scale(1.02)',
                boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText 
              primary="User Dashboard" 
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            />
          </ListItemButton>
          
          {/* Logout Button in Mobile Drawer */}
          <Divider sx={{ my: 1.5, borderColor: 'divider' }} />
          <ListItemButton
            onClick={() => {
              handleMobileNavClick();
              onLogout();
            }}
            sx={{
              borderRadius: 3,
              mb: 1.5,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
              color: 'white',
              fontWeight: 600,
              boxShadow: '0 4px 15px rgba(245, 87, 108, 0.3)',
              "&:hover": {
                background: 'linear-gradient(135deg, #f5576c 0%, #f093fb 100%)',
                transform: 'translateX(8px) scale(1.02)',
                boxShadow: '0 8px 25px rgba(245, 87, 108, 0.4)',
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 40, color: 'white' }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText 
              primary="Logout" 
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: 600,
              }}
            />
          </ListItemButton>
        </List>
      </Drawer>

      {/* Sidebar Drawer (Desktop - Permanent) */}
        <Drawer
          variant="permanent"
          sx={{
            width: { xs: 0, sm: drawerWidth },
            flexShrink: 0,
            display: { xs: 'none', sm: 'block' },
            [`& .MuiDrawer-paper`]: {
              width: drawerWidth,
              boxSizing: "border-box",
              position: "relative",
              // Theme-aware background
            },
          }}
        >
        <Toolbar />
        <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper', opacity: 0.9 }}>
          <Chip 
            label="Admin Dashboard" 
            size="small" 
            icon={<AdminPanelSettingsIcon />}
            sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
              color: 'white',
              fontWeight: 600,
              mb: 2,
              width: '100%',
              height: '32px',
              fontSize: '0.8rem'
            }} 
          />
          <Typography variant="h6" sx={{ 
            fontWeight: 700, 
            color: 'text.primary', 
            textTransform: 'uppercase', 
            letterSpacing: '0.1em', 
            fontSize: '0.8rem',
            textAlign: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}>
            Navigation Menu
          </Typography>
        </Box>
          <List sx={{ px: 2, py: 3 }}>
            {menuItems.map((item, index) => (
              <ListItemButton
                key={item.key}
                component={NavLink}
                to={`/admin/${item.key}`}
                sx={{
                  borderRadius: 3,
                  mb: 1.5,
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: `slideInLeft 0.5s ease-out ${index * 0.1}s both`,
                  "&:hover": {
                    backgroundColor: "rgba(102, 126, 234, 0.12)",
                    transform: 'translateX(8px) scale(1.02)',
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.15)',
                    "&::before": {
                      opacity: 1,
                      transform: 'scaleX(1)',
                    },
                  },
                  "&.active": {
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: "white",
                    fontWeight: "bold",
                    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
                    transform: 'translateX(4px)',
                    "& .MuiListItemIcon-root": {
                      color: "white",
                      transform: 'scale(1.1)',
                    },
                    "&::after": {
                      content: '""',
                      position: 'absolute',
                      right: 0,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: 4,
                      height: '60%',
                      background: 'rgba(255, 255, 255, 0.8)',
                      borderRadius: '2px 0 0 2px',
                    },
                  },
                  "&::before": {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    height: '100%',
                    width: 4,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    transform: 'scaleX(0)',
                    transformOrigin: 'left',
                    transition: 'all 0.3s ease',
                    opacity: 0,
                  },
                }}
              >
              <ListItemIcon sx={{ minWidth: 40 }}>{item.icon}</ListItemIcon>
              <ListItemText 
                primary={item.label} 
                primaryTypographyProps={{
                  fontSize: '0.9rem',
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          ))}
        </List>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{ 
          flexGrow: 1, 
          width: { xs: '100%', sm: `calc(100% - ${drawerWidth}px)` },
          bgcolor: 'background.default',
          minHeight: '100vh',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Toolbar />
        <Container maxWidth="xl" sx={{ p: { xs: 2, sm: 4 }, position: 'relative', zIndex: 1 }}>
          <Paper 
            elevation={0}
            sx={{
              borderRadius: 3,
              bgcolor: 'background.paper',
              backdropFilter: 'blur(10px)',
              overflow: 'hidden',
            }}
          >
            <Routes>
              <Route path="dashboard" element={<AdminDashboardHome />} />
              <Route path="users" element={<AdminUsersList />} />
              <Route path="user-details/:id" element={<AdminUserDetails />} />
              <Route path="videos" element={<AdminVideosList />} />
              <Route path="pending-videos" element={<AdminPendingVideos />} />
              {/* ❌ Video Player route removed */}
              <Route path="token-rewards" element={<AdminTokenRewards />} />
              <Route path="conversion-summary" element={<AdminConversionSummary />} />
              <Route path="conversion-history" element={<AdminConversionHistory />} />
              {/* ❌ App Regions route removed */}
              <Route path="ad-payment" element={<AdminAdPayment />} />
              <Route path="ad-payments-list" element={<AdminAdPaymentsList />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="payment-methods" element={<AdminPaymentMethods />} />
              <Route path="video-categories" element={<AdminVideoCategories />} />
              <Route path="country-kyc" element={<AdminCountryKYC />} />
              <Route path="pending-clicks" element={<AdminPendingClicks />} />
              <Route path="jazzcash" element={<AdminJazzCash />} />
              <Route path="referral-rebalance" element={<AdminReferralRebalance />} />
              <Route path="*" element={<AdminDashboardHome />} />
            </Routes>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}

export default AdminDashboard;
